import { ChangeDetectorRef, computed, Directive, effect, inject, input, signal } from "@angular/core";
import { AbstractAgent, AGUIConnectNotImplementedError, Message } from "@ag-ui/client";
import { DEFAULT_AGENT_ID, randomUUID } from "@copilotkit/shared";
import { AgentKitService, ChatState, injectAgentStore } from "../../../../agent-ui";

/**
 * Abstract base class for chat container components.
 * Encapsulates CopilotKit / AG-UI stream wiring, agent-store binding, thread
 * management and the default submit/change-input lifecycle so that concrete
 * containers only need to add domain-specific behaviour.
 *
 * Use `@Directive()` (no selector) so Angular's DI and signal-input machinery
 * works without making this a rendered component.
 *
 * @example
 * ```typescript
 * \@Component({ ... })
 * export class ChatContainerComponent extends ChatCore implements OnInit {
 *   override async submitInput(value: string): Promise<void> {
 *     // custom pre/post logic, then:
 *     await super.submitInput(value);
 *   }
 * }
 * ```
 */
@Directive()
export abstract class ChatCore implements ChatState {
  /** Current text value held in the chat input field. */
  readonly inputValue = signal<string>("");

  /** ID of the agent whose store this component tracks. Defaults to the global default agent. */
  readonly agentId = input<string | undefined>();

  /** Thread ID forwarded to the underlying abstract agent on every change. */
  readonly threadId = input<string | undefined>();

  /** Resolved agent ID — falls back to CopilotKit's DEFAULT_AGENT_ID when no input is provided. */
  protected readonly resolvedAgentId = computed(() => this.agentId() ?? DEFAULT_AGENT_ID);

  /**
   * Reactive signal that always points to the shared AgentStore for the resolved agent.
   * The store owns messages, running state, run-errors and the AbstractAgent reference.
   */
  readonly agentStore = injectAgentStore(this.resolvedAgentId);

  /** Controls the typing-cursor visibility while an agent run is in flight. */
  protected readonly showCursor = signal<boolean>(false);

  /** Shared AgentKit service — available to subclasses without re-injection. */
  protected readonly agentKitService = inject(AgentKitService);

  /** ChangeDetectorRef — needed after async operations under OnPush. */
  protected readonly cdr = inject(ChangeDetectorRef);

  /** Fallback thread ID generated once per instance when no threadId input is provided. */
  protected readonly generatedThreadId: string = randomUUID();

  /** Guards against calling connectAgent more than once per agent instance. */
  private hasConnectedOnce = false;

  constructor() {
    // Sync the agent's threadId whenever the store or threadId input changes, and
    // trigger a one-time connect handshake for CopilotKit agents.
    // Guards on headers.Authorization so connectAgent is never called before the
    // auth token is available — prevents 401s on startup when the component is
    // instantiated (e.g. inside a [hidden] toolbar panel) before login completes.
    effect(() => {
      const agent = this.agentStore().agent;
      const headers = this.agentKitService.headers();
      if (!agent || !headers.Authorization) return;

      agent.threadId = this.threadId() ?? this.generatedThreadId;

      if (!this.hasConnectedOnce) {
        this.hasConnectedOnce = true;
        if ("isCopilotKitAgent" in (agent as object)) {
          void this.connectToAgent(agent);
        }
      }
    });

    // While the agent is streaming a response, hide the manual cursor — the
    // running state provides its own visual indicator.
    effect(() => {
      if (this.agentStore().isRunning()) {
        this.showCursor.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Appends a user message and triggers an agent run.
   * Override in subclasses to add attachment handling, HITL detection, etc.
   *
   * @param value - The plain-text message submitted by the user.
   */
  async submitInput(value: string): Promise<void> {
    const agent = this.agentStore().agent;
    if (!agent || !value.trim()) return;

    const userMessage: Message = {
      id: randomUUID(),
      role: "user",
      content: value,
    };
    agent.addMessage(userMessage);

    this.changeInput("");
    this.showCursor.set(true);
    this.cdr.markForCheck();

    try {
      await this.agentKitService.core.runAgent({ agent });
    } catch (error) {
      console.error("[ChatCore] submitInput error:", error);
    } finally {
      this.showCursor.set(false);
      this.cdr.markForCheck();
    }
  }

  /**
   * Updates the current text value in the chat input.
   *
   * @param value - New input text.
   */
  changeInput(value: string): void {
    this.inputValue.set(value);
  }

  /**
   * Performs the initial connect handshake for CopilotKit agents.
   * Called at most once per agent instance, from the threadId-sync effect.
   *
   * @param agent - The abstract agent to connect.
   */
  private async connectToAgent(agent: AbstractAgent): Promise<void> {
    this.showCursor.set(true);
    this.cdr.markForCheck();

    try {
      await this.agentKitService.core.connectAgent({ agent });
    } catch (error) {
      if (!(error instanceof AGUIConnectNotImplementedError)) {
        console.error("[ChatCore] Failed to connect to agent:", error);
      }
    } finally {
      this.showCursor.set(false);
      this.cdr.markForCheck();
    }
  }
}
